import React, { lazy, Suspense } from 'react';
import { Container, Typography, Box, Paper, Divider, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { motion, LazyMotion, domAnimation } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';

// Improved performance by using LazyMotion with domAnimation
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="200px">
    <CircularProgress />
  </Box>
);

const About: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const stats = [
    { label: 'Integrity', value: '95%', icon: '🔒' },
    { label: 'Operational Excellence', value: '99%', icon: '⭐' },
    { label: 'Innovation', value: '95%', icon: '💡' },
  ];

  const values = [
    {
      title: 'Quality',
      description: 'We are committed to delivering high-quality services that meet or exceed customer expectations.',
      icon: <VerifiedIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
    },
    {
      title: 'Innovation',
      description: 'We embrace innovation and continuously seek new methods to enhance our services and solutions.',
      icon: <ShowChartIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
    },
    {
      title: 'Integrity',
      description: 'We uphold the highest standards of integrity in all our interactions and business practices.',
      icon: <BusinessCenterIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
    },
    {
      title: 'Customer Focus',
      description: 'We place our customers at the center of everything we do and tailor our services to meet their unique needs.',
      icon: <PeopleIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
    },
    {
      title: 'Excellence',
      description: 'We strive for excellence in all aspects of our operations and professional development.',
      icon: <SchoolIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
    }
  ];

  // Optimized animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <LazyMotion features={domAnimation}>
      <Box sx={{ overflow: 'hidden' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
            color: 'white',
            py: 12,
            position: 'relative',
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              transition={{ duration: 0.5 }}
            >
              <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
                Process-Driven Company delivering superior, sustained and ground-breaking solutions
              </Typography>
            </motion.div>
          </Container>
          
          {/* Decorative Elements - Simplified */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1
          }} />
        </Box>

        <Container maxWidth="lg">
          {/* Mission and Vision Section */}
          <Box sx={{ py: 10 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 5, mt: 4 }}>
              <Box sx={{ flex: '1 1 400px' }}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ duration: 0.5 }}
                >
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 5, 
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      width: '100%', 
                      height: '5px',
                      background: 'linear-gradient(90deg, #1976d2, #2196f3)'
                    }} />
                    
                    <Typography 
                      variant="overline" 
                      fontWeight="bold" 
                      color="primary"
                      sx={{ letterSpacing: 2, mb: 1, display: 'block' }}
                    >
                      OUR PURPOSE
                    </Typography>
                    <Typography variant="h4" gutterBottom fontWeight="bold" color="#0c2340">
                      Our Mission
                    </Typography>
                    <Divider sx={{ width: '80px', borderWidth: 2, borderColor: '#1976d2', my: 3 }} />
                    
                    <Typography paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
                      Delivering world-class CMMI, ISO, digital, HR, and managed services—enabling organizations and professionals to thrive in a competitive, technology-driven world.
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>

              <Box sx={{ flex: '1 1 400px' }}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 5, 
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      width: '100%', 
                      height: '5px',
                      background: 'linear-gradient(90deg, #1976d2, #2196f3)'
                    }} />
                    
                    <Typography 
                      variant="overline" 
                      fontWeight="bold" 
                      color="primary"
                      sx={{ letterSpacing: 2, mb: 1, display: 'block' }}
                    >
                      OUR ASPIRATIONS
                    </Typography>
                    <Typography variant="h4" gutterBottom fontWeight="bold" color="#0c2340">
                      Our Vision
                    </Typography>
                    <Divider sx={{ width: '80px', borderWidth: 2, borderColor: '#1976d2', my: 3 }} />
                    
                    <Typography paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
                      Empowering global growth through process excellence, innovation, and digital transformation.
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            </Box>
          </Box>

          {/* Our Values Section - Optimized */}
          <Box 
            component="section" 
            sx={{ 
              bgcolor: '#f7f9fc', 
              py: 8,
              mb: 8
            }}
          >
            <Container>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                  Our Values
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto' }}>
                  Our core values define who we are and guide everything we do at Right Track Technologies Limited.
                </Typography>
              </Box>
              
              <Box 
                component={motion.div}
                initial="hidden"
                whileInView="visible"
                variants={fadeIn}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { 
                    xs: '1fr', 
                    sm: 'repeat(2, 1fr)', 
                    md: 'repeat(3, 1fr)' 
                  },
                  gap: 4
                }}
              >
                {values.map((value, index) => (
                  <Box 
                    key={index}
                    component={motion.div}
                    variants={fadeIn}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    sx={{
                      bgcolor: 'white',
                      p: 4,
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease-in-out',
                      },
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      {value.icon}
                    </Box>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      {value.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Container>
          </Box>

          {/* Stats Section - Optimized */}
          <Box sx={{ mt: 8, mb: 10 }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={fadeIn}
              viewport={{ once: true }}
              transition={{ duration: 0.3 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                textAlign="center"
                fontWeight="bold"
                gutterBottom
                sx={{ mb: 6 }}
              >
                Our Success Metrics
              </Typography>
            </motion.div>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {stats.map((stat, index) => (
                <Box key={index} sx={{ flex: '1 1 300px' }}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    variants={fadeIn}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 5, 
                        textAlign: 'center',
                        borderRadius: '16px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-10px)',
                          boxShadow: '0 15px 50px rgba(0,0,0,0.15)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <Typography 
                          sx={{ 
                            fontSize: '5rem',
                            position: 'absolute',
                            top: '-70px',
                            right: '-20px',
                            opacity: 0.1,
                            transform: 'rotate(10deg)',
                          }}
                        >
                          {stat.icon}
                        </Typography>
                        <Typography 
                          variant="h3" 
                          color="primary" 
                          gutterBottom 
                          fontWeight="bold"
                          sx={{ 
                            fontSize: '3.5rem',
                            background: 'linear-gradient(90deg, #1976d2, #2196f3)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold"
                          color="#0c2340"
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Paper>
                  </motion.div>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>

        {/* Our Team Section */}
        <Box
          sx={{
            bgcolor: '#0c0c0c',
            color: 'white',
            py: 10,
            position: 'relative',
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={fadeIn}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 'bold',
                  color: '#ff1f8e',
                  mb: 4
                }}
              >
                Our Team
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                <Box sx={{ flex: '1 1 500px' }}>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
                    Our highly skilled team help clients understand risks, identify vulnerabilities and deliver critical cyber security and data protection solutions that protect their organizations. At Right Track Technologies Limited our focus is to help our clients and for our clients to trust us as their partner to provide them with the best service and advice in the most efficient way.
                  </Typography>
                  
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
                    We are providing a reliable, high-quality security and risk consulting services since long. We help our customers protect not just their data, but their reputation, customer trust and bottom line.
                  </Typography>
                  
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
                    We provide four core service areas: cyber security, data privacy, penetration testing/red team assessments and managed security services. We and our expert team of professionals possess specialist industry experience and proven capabilities, delivering effective cyber security and data privacy services across financial services, manufacturing, technology, retail, media, government and 24×7 critical services.
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    flex: '1 1 400px',
                    display: { xs: 'none', md: 'block' }
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                  >
                    <Box 
                      component="img"
                      src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                      alt="Professional Team"
                      sx={{
                        width: '100%',
                        borderRadius: '16px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                      }}
                    />
                  </motion.div>
                </Box>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </LazyMotion>
  );
};

export default About; 